import React from 'react';
import { of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { get } from 'lodash-es';
import { defineMessages, FormattedMessage } from 'react-intl';

import { ModalService } from 'ui-components/services/modal-service';

import { GenericErrorRetryContainer } from '../containers/generic-error-retry-container';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { FulfillmentPlanOrderGridStatus } from '../enums/fulfillmentplan-order-grid-status';
import { PackageService } from '../services/package-service';
import Global from '../store/global-selectors';
import { selectOrderDetailsSalesOrderId } from '../store/orders/order-details/orders.details.selectors';
import { getOrderDetailsSuccess, getOrderDetailsFailure } from '../store/orders/order-details/orders.details.actions';

import { bulkLoadSalesOrders$ } from '../store/bulkload/bulkload.observables';

import { getActualSalesOrderIds } from '../store/orders/orders.helpers';

const messages = defineMessages({
  creatingLabelTitle: {
    description: 'orderDetailsEpics.creatingLabelTitle',
    defaultMessage: 'Processing...',
    id: 'BmyaSk',
  },
  creatingLabelContent: {
    description: 'orderDetailsEpics.creatingLabelContent',
    defaultMessage: 'Purchasing {labelCount, plural, one {label from carrier.} other {labels from carrier.}}',
    id: 'KLHlkq',
  },
  orderDetailsErrorTitle: {
    description: 'orderDetailsEpics.orderDetailsErrorTitle',
    defaultMessage: 'Error',
    id: 'OLhtMw',
  },
  orderDetailsErrorMessage: {
    description: 'orderDetailsEpics.orderDetailsErrorMessage',
    defaultMessage: 'We were unable to open Order Details. Please try again.',
    id: '+HjnqM',
  },
});

class OrderDetailsHelperServiceClass {
  buildCswPersistPayload(fulfillmentPlan, accountPackages) {
    const packages = get(fulfillmentPlan, 'packages') || emptyArray;
    const options = get(fulfillmentPlan, 'options') || {};
    const updatePayload = {
      shipFromId: get(fulfillmentPlan, 'shipFromId'),
      serviceId: get(fulfillmentPlan, 'serviceId'),
      sellerLabelProviderId: get(fulfillmentPlan, 'sellerLabelProviderId'),
      options: this.buildOtherShipmentOptions(options),
      packingSlipTemplateId: get(fulfillmentPlan, 'packingSlipTemplateId'),
      deliveryEmailNotificationTemplateId: get(fulfillmentPlan, 'deliveryEmailNotificationTemplateId'),
      shipmentEmailNotificationTemplateId: get(fulfillmentPlan, 'shipmentEmailNotificationTemplateId'),
      rate: get(fulfillmentPlan, 'rateSummary.rate'),
      insuranceProvider: get(fulfillmentPlan, 'insuranceProvider'),
      properties: get(fulfillmentPlan, 'properties'),
      customs: get(fulfillmentPlan, 'customs'),
    };

    if (packages.length === 0) return updatePayload;

    const isMultipackage = packages.length > 1;
    if (isMultipackage) {
      const multipackage = [];
      const weight = { value: 0 };
      packages.forEach((pack) => {
        const realPackage = PackageService.findBestGuessPackageByPackageTypeId(pack.packageTypeId, accountPackages);
        const newPackage = {};
        newPackage.weight = get(pack, 'weight');
        if (newPackage.weight) {
          weight.unit = newPackage.weight.unit;
          weight.value += newPackage.weight.value;
        }
        newPackage.dimensions = get(pack, 'dimensions');
        newPackage.insuredValue = get(pack, 'insuredValue');
        if (realPackage)
          newPackage.packageId = `${get(pack, 'packageTypeId', 'null')}-${get(realPackage, 'isCustom', 'false')}-${realPackage.carrierId || 'null'}`;
        multipackage.push(newPackage);
      });
      updatePayload.weight = weight;
      updatePayload.multipackage = {
        packages: multipackage,
      };
      return updatePayload;
    }

    updatePayload.multipackage = undefined;
    const singlePackage = packages[0];
    const realPackage = PackageService.findBestGuessPackageByPackageTypeId(singlePackage.packageTypeId, accountPackages);
    updatePayload.weight = get(singlePackage, 'weight');
    updatePayload.packages = packages;

    if (realPackage) {
      updatePayload.packages = packages.map((p) => ({
        ...p,
        packageId: `${get(p, 'packageTypeId', 'null')}-${get(realPackage, 'isCustom', 'false')}-${realPackage.carrierId || 'null'}`,
      }));
    }

    return updatePayload;
  }

  isPendingFulfillmentPlan(fulfillmentPlan) {
    const pendingFulfillmentPlanStatuses = [
      FulfillmentPlanOrderGridStatus.AwaitingShipment.name,
      FulfillmentPlanOrderGridStatus.AwaitingPayment.name,
      FulfillmentPlanOrderGridStatus.OnHold.name,
    ];
    const fpOrderGridStatus = get(fulfillmentPlan, 'orderGridStatus');
    return !!pendingFulfillmentPlanStatuses.includes(fpOrderGridStatus);
  }

  getFulfillmentPlanByOrderGridStatus(fulfillmentPlans, orderGridStatus) {
    return fulfillmentPlans.find((plan) => get(plan, 'orderGridStatus') === orderGridStatus);
  }

  getAwaitingShipmentFulfillmentPlan(fulfillmentPlans = emptyArray) {
    return this.getFulfillmentPlanByOrderGridStatus(fulfillmentPlans, FulfillmentPlanOrderGridStatus.AwaitingShipment.name);
  }

  getAwaitingPaymentFulfillmentPlan(fulfillmentPlans = emptyArray) {
    return this.getFulfillmentPlanByOrderGridStatus(fulfillmentPlans, FulfillmentPlanOrderGridStatus.AwaitingPayment.name);
  }

  getOnHolFulfillmentPlan(fulfillmentPlans = emptyArray) {
    return this.getFulfillmentPlanByOrderGridStatus(fulfillmentPlans, FulfillmentPlanOrderGridStatus.OnHold.name);
  }

  findPendingFulfillmentPlan(fulfillmentPlans = emptyArray, selectedFulfillmentPlan = emptyObject) {
    const selectedIsPendingFulfillmentPlan = this.isPendingFulfillmentPlan(selectedFulfillmentPlan);
    if (selectedIsPendingFulfillmentPlan) {
      return selectedFulfillmentPlan;
    }
    const awaitingShipmentFP = this.getAwaitingShipmentFulfillmentPlan(fulfillmentPlans);
    if (awaitingShipmentFP) {
      return awaitingShipmentFP;
    }
    const awaitingPaymentFP = this.getAwaitingPaymentFulfillmentPlan(fulfillmentPlans);
    if (awaitingPaymentFP) {
      return awaitingPaymentFP;
    }
    const onHoldFP = this.getOnHolFulfillmentPlan(fulfillmentPlans);
    if (onHoldFP) {
      return onHoldFP;
    }
  }

  buildOtherShipmentOptions(options) {
    const built = {};
    if (options) {
      Object.keys(options)
        .filter((option) => options[option])
        .forEach((option) => (built[option] = options[option]));
    }
    return built;
  }

  findSalesOrderItem(fulfillmentPlanItem, salesOrderItems) {
    // we are using the get for itemSku to check for empty strings
    const itemSku = get(fulfillmentPlanItem, 'sku');
    const bySku = salesOrderItems.find((item) => item.sku === fulfillmentPlanItem.sku);
    if (itemSku && bySku) {
      return get(bySku, 'salesOrderItemId');
    }
    return get(
      salesOrderItems.find((item) => item.name === fulfillmentPlanItem.name),
      'salesOrderItemId',
    );
  }

  // TODO: the below logic for salesOrderItemId assignment is shared with the split process and ship-together due to limitations on the back-end - refactor or remove once back-end is ready
  assignSalesOrderItemId(fulfillmentPlanItem, salesOrderItems) {
    return fulfillmentPlanItem.salesOrderItemId || this.findSalesOrderItem(fulfillmentPlanItem, salesOrderItems);
  }

  determineDefaultPackingSlipFormat(orderStore, sellerPackingSlipFormat, packingSlips, cswPackingSlipId) {
    const storePackingSlip = get(orderStore, 'packingSlip');
    const storeMatchesSeller = sellerPackingSlipFormat && sellerPackingSlipFormat.includes(get(storePackingSlip, 'size'));
    const defaultShipStationPackingSlip = packingSlips.find((x) => x.isShared) ?? packingSlips[0];
    const defaultPackingSlip = storeMatchesSeller ? storePackingSlip : defaultShipStationPackingSlip;
    return packingSlips.find((x) => x.packingSlipId === cswPackingSlipId) || defaultPackingSlip;
  }

  determineDefaultShipmentTemplate(orderStore, shipmentTemplates, cswShipmentTemplateId) {
    if (cswShipmentTemplateId !== undefined) {
      return shipmentTemplates.find((template) => template.emailTemplateId === cswShipmentTemplateId);
    }
    return get(orderStore, 'shipmentEmailTemplate');
  }

  determineDefaultDeliveryTemplate(orderStore, deliveryTemplate, cswDeliveryTemplateId) {
    if (cswDeliveryTemplateId !== undefined) {
      return deliveryTemplate.find((template) => template.emailTemplateId === cswDeliveryTemplateId);
    }
    return get(orderStore, 'deliveryEmailTemplate');
  }

  mapConfirmationStringToConfirmationEnumId(fulfillmentPlans = emptyArray) {
    return fulfillmentPlans.map((plan) => {
      const hasConfirmationOption = plan.options && plan.options.confirmation;
      if (!hasConfirmationOption) {
        return plan;
      }
      const { confirmationEnumId } = plan.options;
      return {
        ...plan,
        options: {
          ...plan.options,
          confirmation: confirmationEnumId,
        },
      };
    });
  }

  mapConfirmationTextToLineItem(order, carriers) {
    const { carrierId, confirmationEnumId } = order;
    const selectedCarrier = carriers.find((carrier) => carrier?.carrierId === carrierId);
    const confirmations = get(selectedCarrier, 'confirmations') || emptyArray;
    const confirmationObject = confirmations.find((confirmation) => parseInt(confirmation.confirmationId, 10) === confirmationEnumId);
    return {
      ...order,
      confirmationDisplayText: confirmationObject && confirmationObject.name,
    };
  }

  mapConfirmationTextToOrder = (orders, carriers) => {
    return orders.map((order) => {
      if (Array.isArray(order)) {
        return this.mapConfirmationTextToOrder(order, carriers);
      }

      return this.mapConfirmationTextToLineItem(order, carriers);
    });
  };

  getTargetSalesOrderIdForOrderDetails(state) {
    const orderIds = Global.selectSelectedOrdersIds(state);
    const actualSalesOrderId = getActualSalesOrderIds(state, orderIds);
    const salesOrderId = selectOrderDetailsSalesOrderId(state);
    // may not exist in state initially when we first open the OD modal

    const actualSelectedOrderId = salesOrderId && actualSalesOrderId.find((val) => val === salesOrderId);
    return actualSelectedOrderId || actualSalesOrderId[0] || salesOrderId;
  }

  returnSalesOrderFromMemory(state, entitiesSalesOrder, action, fulfillmentPlanId) {
    const suppressRouting = get(action, 'suppressRouting', false);
    return getOrderDetailsSuccess(suppressRouting, fulfillmentPlanId);
  }

  returnNewlyFetchedOrderDetails(state, salesOrderId, action, fulfillmentPlanId) {
    const suppressRouting = get(action, 'suppressRouting', false);
    return bulkLoadSalesOrders$([salesOrderId]).pipe(
      map(() => {
        return getOrderDetailsSuccess(suppressRouting, fulfillmentPlanId);
      }),
      catchError((error) => {
        ModalService.launchModal({
          component: GenericErrorRetryContainer,
          props: {
            action,
            title: <FormattedMessage {...messages.orderDetailsErrorTitle} />,
            message: <FormattedMessage {...messages.orderDetailsErrorMessage} />,
          },
        });
        return of(getOrderDetailsFailure(error));
      }),
    );
  }
}

export const OrderDetailsHelperService = new OrderDetailsHelperServiceClass();
